import { Alert, Button, Form, Modal, ModalProps } from 'antd';
import { AlertStatus } from './VehicleAlert';
import { useEffect, useState } from 'react';
import { getAccessoryId, parseAccessoryId } from './getAccessoryId';
import { VehicleFormValues, VehicleReturnValues } from './VehicleModal.types';
import VehicleFormWrapper from './VehicleFormWrapper';
import confirmModal from '../ConfirmBackModal';
import {
  useCustomerVehicle,
  useVehicleEnrich,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';

interface Props extends Omit<ModalProps, 'onOk' | 'onCancel'> {
  customerId?: number;
  vehicleId?: number;
  onCancel?: (refetch: boolean) => void;
  onOk?: (vehicle: VehicleReturnValues) => Promise<void>;
  consultLicensePlate?: boolean;
}

const VehicleModal: React.FC<Props> = ({
  customerId,
  vehicleId,
  onCancel,
  consultLicensePlate = false,
  ...props
}) => {
  const [form] = Form.useForm<VehicleFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  const vehicleEnrich = useVehicleEnrich();
  const validateFragaPlate = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;

  const { data: vehicle } = useCustomerVehicle(
    customerId,
    vehicleId,
    props.open,
  );

  useEffect(() => {
    if (vehicle && props.open) {
      form.setFieldsValue({
        ...vehicle,
        vehicleId: vehicle.id,
        licensePlate: vehicle.licensePlate,
        brandId: vehicle.model.brand.id,
        chassi: vehicle.chassi,
        colorId: vehicle.color.id,
        fleet: vehicle.fleet,
        fuelId: vehicle.fuel.id,
        km: vehicle.km,
        averageKm: vehicle.averageKm,
        modelId: vehicle.model.id,
        renavam: vehicle.renavam,
        type: vehicle?.type,
        yearFabrication: String(vehicle.yearFabrication),
        yearModel: String(vehicle.yearModel),
        accessories: !!vehicle?.accessories?.length
          ? vehicle.accessories.map((accessory) => getAccessoryId(accessory))
          : undefined,
        horsePower: !!vehicle.horsePower?.length
          ? vehicle.horsePower
          : undefined,
      });
    }
  }, [vehicle, props.open]);

  function handleCancel(refetch: boolean) {
    form.resetFields();
    setDisabled(false);
    setIsLoading(false);
    setFormChanged(false);
    !!onCancel && onCancel(refetch);
  }

  const enrichConfirm = () => {
    Modal.confirm({
      title: 'Atualização de veículo',
      content:
        'Ao atualizar, os dados atuais do veículo serão substituídos. Deseja continuar?',
      onOk: () => {
        if (customerId && vehicleId)
          vehicleEnrich.mutateAsync({ customerId, vehicleId });
      },
      okText: 'Atualizar',
    });
  };

  async function handleOk() {
    setIsLoading(true);
    try {
      const values = await form.validateFields();

      if (props.onOk) {
        await props.onOk({
          ...values,
          accessories: values.accessories?.map((accessory) =>
            parseAccessoryId(accessory),
          ),
          chassi: values.chassi || undefined,
          cylinders: values.cylinders || undefined,
          engineCapacity: values.engineCapacity || undefined,
          engineName: values.engineName || undefined,
          engineTechnicalCode: values.engineTechnicalCode || undefined,
          release: values.release || undefined,
        });
      }

      handleCancel(false);
    } catch {
    } finally {
      setIsLoading(false);
    }
  }

  function onChangeAlert(status?: AlertStatus) {
    if (!props.open) return;

    if (status !== undefined) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }

  function handleFormChange() {
    setFormChanged(!!vehicle);
  }

  return (
    <Modal
      {...props}
      title={props.title || `${vehicle ? 'Edição' : 'Cadastro'} de veículo`}
      onCancel={() => {
        if (formChanged) {
          return confirmModal(() => handleCancel(false), {
            content:
              'Há alterações não salvas no formulário. Tem certeza de que deseja fechar sem salvá-las?',
          });
        }
        handleCancel(false);
      }}
      onOk={handleOk}
      okButtonProps={{ disabled }}
      confirmLoading={isLoading}
      okText="Salvar"
      width={1200}
      destroyOnClose
      key={Number(!!props.open)}
    >
      {!!vehicle?.hasMissingData &&
        validateFragaPlate.test(vehicle?.licensePlate) && (
          <Alert
            type="warning"
            style={{ marginBottom: 24 }}
            description={
              <>
                <p>
                  Foram encontradas novas informações para este veículo. Deseja
                  atualizar os dados?
                </p>
                <Button
                  type="primary"
                  onClick={enrichConfirm}
                  loading={vehicleEnrich.isLoading}
                >
                  Atualizar dados
                </Button>
              </>
            }
          />
        )}

      <VehicleFormWrapper
        form={form}
        customerId={customerId}
        vehicle={vehicle}
        onChangeAlert={onChangeAlert}
        onChangeVehicle={() => handleCancel(true)}
        handleFormChange={handleFormChange}
        consultLicensePlate={consultLicensePlate}
      />
    </Modal>
  );
};

export default VehicleModal;
